/**
 * 帳票 PDF 保存時のファイル名生成。
 *
 * 資金収支（第一号第一〜第四様式）の正しい形式（岩切さん指摘・旧システム準拠）:
 *   `{和暦年度}_{主体名}_{様式名}.pdf`
 * 例: `令和7年度_社会福祉法人 竹の子福祉会_第一号第一様式.pdf`
 *
 * 事業活動計算書（第二号第一／第四様式）も同じ規則:
 * 例: `令和7年度_社会福祉法人 竹の子福祉会_第二号第一様式.pdf`
 *
 * 主体名:
 *   - 第一〜第三様式 / 第二号第一様式: 法人名
 *   - 第四様式（第一号・第二号）: 拠点（施設）名
 */

export type CashFlowFormType = 'first' | 'fourth';
export type CashFlowBreakdownLevel = 'jigyo' | 'kyoten';

export interface ReportPdfNameSource {
  id?: string;
  name: string;
  cashFlowFormType?: CashFlowFormType;
  cashFlowBreakdownLevel?: CashFlowBreakdownLevel;
}

/** 会計年度（4月始まり）の西暦開始年を period_end から求める */
export function fiscalYearStartFromPeriodEnd(periodEnd: string | Date): number {
  const d = typeof periodEnd === 'string' ? parseIsoDate(periodEnd) : periodEnd;
  if (Number.isNaN(d.getTime())) {
    throw new Error(`Invalid period end: ${periodEnd}`);
  }
  return d.getMonth() < 3 ? d.getFullYear() - 1 : d.getFullYear();
}

/** 西暦年度開始年 → 和暦「令和N年度」等 */
export function toWarekiFiscalYearLabel(fiscalYearStart: number): string {
  // 令和元年 = 2019 → year - 2018
  if (fiscalYearStart >= 2019) {
    return `令和${fiscalYearStart - 2018}年度`;
  }
  if (fiscalYearStart >= 1989) {
    return `平成${fiscalYearStart - 1988}年度`;
  }
  return `${fiscalYearStart}年度`;
}

export function corporationNameFromTenantPath(tenantName?: string | null): string {
  if (!tenantName) return '';
  return tenantName.split(' ＞ ')[0].trim();
}

/**
 * 様式ラベル。
 * 事業活動計算書は id/name で第二号を判定し、cashFlowFormType 流用で
 * 第一号に化けないようにする。
 */
export function cashFlowFormStyleLabel(report: ReportPdfNameSource): string | null {
  if (
    report.id === 'business-activity' ||
    /事業活動計算書.*第二号第一様式|第二号第一様式.*事業活動/.test(report.name) ||
    /（第二号第一様式）/.test(report.name)
  ) {
    return '第二号第一様式';
  }
  if (
    report.id === 'business-activity-fourth' ||
    /（第二号第四様式）/.test(report.name)
  ) {
    return '第二号第四様式';
  }

  if (report.cashFlowFormType === 'first') return '第一号第一様式';
  if (report.cashFlowBreakdownLevel === 'jigyo') return '第一号第二様式';
  if (report.cashFlowBreakdownLevel === 'kyoten') return '第一号第三様式';
  if (report.cashFlowFormType === 'fourth') return '第一号第四様式';

  const matched = report.name.match(/（(第[一二]号第[一二三四]様式)）/);
  return matched?.[1] ?? null;
}

/**
 * 年度_主体名_様式.pdf を組み立てる。
 * 必須パーツが欠けていれば null（呼び出し側で従来名へフォールバック）。
 */
export function buildFormStylePdfFilename(parts: {
  warekiFiscalYearLabel: string;
  entityName: string;
  formStyle: string;
}): string | null {
  const year = parts.warekiFiscalYearLabel?.trim();
  const entity = parts.entityName?.trim();
  const style = parts.formStyle?.trim();
  if (!year || !entity || !style) return null;
  return `${year}_${entity}_${style}.pdf`;
}

export function resolveCashFlowPdfFilename(
  report: ReportPdfNameSource,
  options: {
    periodEnd: string;
    corporationName: string;
    facilityName?: string | null;
  },
): string | null {
  const formStyle = cashFlowFormStyleLabel(report);
  if (!formStyle) return null;

  const useFacility =
    formStyle === '第一号第四様式' || formStyle === '第二号第四様式';
  const entityName = useFacility
    ? (options.facilityName?.trim() || options.corporationName)
    : options.corporationName;

  const warekiFiscalYearLabel = toWarekiFiscalYearLabel(
    fiscalYearStartFromPeriodEnd(options.periodEnd),
  );

  return buildFormStylePdfFilename({
    warekiFiscalYearLabel,
    entityName,
    formStyle,
  });
}

function parseIsoDate(iso: string): Date {
  // YYYY-MM-DD をローカル日付として扱う（UTCずれ防止）
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  return new Date(iso);
}
